/**
 * QUIPWA — OAuth 2.0 helpers (pure functions)
 *
 * QUIPWA has no backend, so it uses Google's browser-only token flow with a
 * full-page redirect (no popup, no hidden iframe):
 *
 *   app ──redirect──► accounts.google.com ──redirect──► /auth/callback#access_token=…
 *
 * Everything here is pure (URL in, data out) so it is unit-tested without a
 * browser. Side effects (navigation, storage) live in auth-session.ts.
 *
 * Why not the hidden-iframe "silent refresh": it relied on third-party cookies
 * (blocked by browsers) and was blocked by our own COEP header; it hung for
 * 10 s and then signed the user out. Reconnecting is a top-level redirect with
 * `prompt=none`, which needs no third-party cookie.
 */

export const AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth'
export const USERINFO_ENDPOINT = 'https://www.googleapis.com/oauth2/v2/userinfo'

export const SCOPES = {
  /** Read/write only files QUIPWA created or the user opened with it. Non-sensitive. */
  driveFile: 'https://www.googleapis.com/auth/drive.file',
  /** Hidden per-app folder for internal state. Non-sensitive. */
  driveAppData: 'https://www.googleapis.com/auth/drive.appdata',
  /** Study reminders in Google Tasks. Sensitive: requested only when reminders are on. */
  tasks: 'https://www.googleapis.com/auth/tasks',
  email: 'https://www.googleapis.com/auth/userinfo.email',
  profile: 'https://www.googleapis.com/auth/userinfo.profile',
} as const

/** Without these the app cannot sync at all. */
export const REQUIRED_SCOPES: readonly string[] = [SCOPES.driveFile, SCOPES.email]
/** Requested on sign-in; the app degrades gracefully without them. */
export const DEFAULT_SCOPES: readonly string[] = [SCOPES.driveFile, SCOPES.driveAppData, SCOPES.email, SCOPES.profile]

export type Prompt = 'none' | 'consent' | 'select_account'

export interface AuthUrlInput {
  clientId: string
  redirectUri: string
  scopes: readonly string[]
  state: string
  prompt?: Prompt | Prompt[]
  loginHint?: string | null
}

export function buildAuthUrl(input: AuthUrlInput): string {
  const params = new URLSearchParams({
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    response_type: 'token',
    scope: [...new Set(input.scopes)].join(' '),
    state: input.state,
    // Keep everything granted earlier (e.g. Tasks) when asking for more.
    include_granted_scopes: 'true',
  })
  if (input.prompt) params.set('prompt', Array.isArray(input.prompt) ? input.prompt.join(' ') : input.prompt)
  if (input.loginHint) params.set('login_hint', input.loginHint)
  return `${AUTH_ENDPOINT}?${params.toString()}`
}

export type CallbackResult =
  | { kind: 'token'; accessToken: string; expiresInSeconds: number; state: string | null; grantedScopes: string[] }
  | { kind: 'error'; error: string; state: string | null }
  | { kind: 'empty' }

/**
 * Read the result Google put in the redirect. Tokens arrive in the fragment
 * (`#access_token=…`); errors may come in the fragment or the query string.
 */
export function parseCallback(hash: string, search: string): CallbackResult {
  const h = new URLSearchParams(hash.replace(/^#/, '').replace(/^\/auth\/callback\??/, ''))
  const s = new URLSearchParams(search.replace(/^\?/, ''))
  const error = h.get('error') ?? s.get('error')
  const state = h.get('state') ?? s.get('state')
  if (error) return { kind: 'error', error, state }

  const accessToken = h.get('access_token')
  if (!accessToken) return { kind: 'empty' }
  const expires = Number(h.get('expires_in') ?? '3600')
  return {
    kind: 'token',
    accessToken,
    expiresInSeconds: Number.isFinite(expires) && expires > 0 ? expires : 3600,
    state,
    grantedScopes: (h.get('scope') ?? '').split(/\s+/).filter(Boolean),
  }
}

/** Required scopes the user did not grant (Google lets users untick permissions). */
export function missingScopes(granted: readonly string[], required: readonly string[] = REQUIRED_SCOPES): string[] {
  const have = new Set(granted)
  return required.filter((s) => !have.has(s))
}

/**
 * Errors from a `prompt=none` attempt that mean "the user has to see Google's
 * screen", as opposed to real failures.
 */
export function needsInteraction(error: string): boolean {
  return ['interaction_required', 'login_required', 'consent_required', 'account_selection_required'].includes(error)
}

export function randomState(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** sessionStorage keys where main.tsx parks Google's redirect before the hash router runs. */
export const OAUTH_FRAGMENT_KEY = 'oauth_hash'
export const OAUTH_SEARCH_KEY = 'oauth_search'